import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Upload, Loader2, CheckCircle2, KeyRound, FileUp, Shield } from "lucide-react";
import { toast } from "sonner";
import * as adeApi from "@/services/adeApi";
import type { AdeKeyGenerationResponse } from "@/types/api";

type Step = "account" | "keypair" | "upload" | "done";

interface AddAbmConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function AddAbmConfigDialog({ open, onOpenChange, onSuccess }: AddAbmConfigDialogProps) {
  const [step, setStep] = useState<Step>("account");
  const [accountName, setAccountName] = useState("");
  const [loading, setLoading] = useState(false);
  const [keypairResult, setKeypairResult] = useState<AdeKeyGenerationResponse | null>(null);
  const [tokenFile, setTokenFile] = useState<File | null>(null);

  const reset = () => {
    setStep("account");
    setAccountName("");
    setLoading(false);
    setKeypairResult(null);
    setTokenFile(null);
  };

  const handleClose = (val: boolean) => {
    if (!val) reset();
    onOpenChange(val);
  };

  const handleGenerateKeypair = async () => {
    if (!accountName.trim()) return;
    setLoading(true);
    try {
      const result = await adeApi.generateKeypair({ accountName: accountName.trim() });
      setKeypairResult(result);
      setStep("keypair");
    } catch (err) {
      toast.error("Failed to generate keypair. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPublicKey = () => {
    if (!keypairResult?.publicKeyPem) return;
    const blob = new Blob([keypairResult.publicKeyPem], { type: "application/x-pem-file" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${accountName.replace(/\s+/g, "_")}_public_key.pem`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUploadToken = async () => {
    if (!tokenFile || !keypairResult) return;
    setLoading(true);
    try {
      // keyId from keypair generation is the adeAccountId (UUID) for subsequent calls
      const adeAccountId = keypairResult.keyId || keypairResult.accountName || accountName;
      await adeApi.uploadServerToken(adeAccountId, tokenFile);
      setStep("done");
      toast.success("Apple Business Manager configuration added successfully!");
      onSuccess();
    } catch (err) {
      toast.error("Failed to upload server token. Please verify the file and try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const STEPS: { key: Step; label: string; icon: React.ReactNode }[] = [
    { key: "account", label: "Account", icon: <Shield className="h-4 w-4" /> },
    { key: "keypair", label: "Keypair", icon: <KeyRound className="h-4 w-4" /> },
    { key: "upload", label: "Upload Token", icon: <FileUp className="h-4 w-4" /> },
    { key: "done", label: "Done", icon: <CheckCircle2 className="h-4 w-4" /> },
  ];

  const stepIndex = STEPS.findIndex(s => s.key === step);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Apple Business Manager Configuration</DialogTitle>
          <DialogDescription>
            Connect a new ABM account by generating a keypair and uploading the server token from Apple.
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-1 py-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              {i > 0 && (
                <div className={`h-px w-6 ${i <= stepIndex ? "bg-primary" : "bg-border"}`} />
              )}
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                i === stepIndex
                  ? "bg-primary/10 text-primary"
                  : i < stepIndex
                    ? "text-primary/70"
                    : "text-muted-foreground"
              }`}>
                {s.icon}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className="py-4 space-y-4">
          {step === "account" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="abm-account-name">Account Name</Label>
                <Input
                  id="abm-account-name"
                  placeholder="e.g. My Organization ABM"
                  value={accountName}
                  onChange={e => setAccountName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleGenerateKeypair()}
                />
                <p className="text-xs text-muted-foreground">
                  Enter a friendly name to identify this ABM account in your organization.
                </p>
              </div>
            </div>
          )}

          {step === "keypair" && keypairResult && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  Keypair generated successfully
                </div>
                <p className="text-xs text-muted-foreground">
                  Download the public key below and upload it to your Apple Business Manager portal
                  to get a server token file (.p7m).
                </p>
                <Button variant="outline" size="sm" onClick={handleDownloadPublicKey}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Public Key
                </Button>
              </div>
              <div className="rounded-lg border border-dashed p-4">
                <p className="text-sm font-medium mb-2">Next steps:</p>
                <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Go to <strong>business.apple.com</strong></li>
                  <li>Navigate to Settings → MDM Server</li>
                  <li>Upload the downloaded public key</li>
                  <li>Download the server token (.p7m file)</li>
                  <li>Come back here and click "Continue" to upload it</li>
                </ol>
              </div>
            </div>
          )}

          {step === "upload" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="server-token-file">Server Token File (.p7m)</Label>
                <div className="relative">
                  <Input
                    id="server-token-file"
                    type="file"
                    accept=".p7m,.p7b"
                    onChange={e => setTokenFile(e.target.files?.[0] || null)}
                    className="cursor-pointer"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Upload the server token file you downloaded from Apple Business Manager.
                </p>
              </div>
              {tokenFile && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground rounded-lg border bg-muted/30 p-3">
                  <Upload className="h-4 w-4" />
                  {tokenFile.name} ({(tokenFile.size / 1024).toFixed(1)} KB)
                </div>
              )}
            </div>
          )}

          {step === "done" && (
            <div className="text-center py-6 space-y-3">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
              <h3 className="text-lg font-semibold">Configuration Complete</h3>
              <p className="text-sm text-muted-foreground">
                Your Apple Business Manager account <strong>{accountName}</strong> has been configured
                successfully and is ready to use.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          {step === "account" && (
            <Button onClick={handleGenerateKeypair} disabled={!accountName.trim() || loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Generate Keypair
            </Button>
          )}
          {step === "keypair" && (
            <Button onClick={() => setStep("upload")}>
              Continue to Upload Token
            </Button>
          )}
          {step === "upload" && (
            <Button onClick={handleUploadToken} disabled={!tokenFile || loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Upload & Complete
            </Button>
          )}
          {step === "done" && (
            <Button onClick={() => handleClose(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
